import qs from 'query-string';
import * as lzString from 'lz-string';
import { isRecord, Language, MarketCountryCode, refineFiltersAndOptions, safePanic } from '@ev-tech/search-modules-api';

import { parseParams, SECURITY_LIMITS, validateLanguage, validateMarketCountryCode, type ParsedSearchParams } from './validation';
import { DEFAULT_OPTIONS } from '../consts/searchDefaults';

export type { ParsedSearchParams };

export type SearchUrlParts = {
  countryCode?: MarketCountryCode;
  language?: Language;
  query?: string;
};

export const extractParamsFromUrl = (url: string | URL, silent = false): SearchUrlParts => {
  try {
    if (url.toString().includes('/_next')) {
      return {};
    }
    const _url = typeof url === 'string' ? url : url.toString();

    // We don't have the countryCode and language in the url for the 404 page, so we return the default values.
    if (url.toString() === '/404') {
      return {
        countryCode: DEFAULT_OPTIONS.marketCountryCode,
        language: DEFAULT_OPTIONS.language,
      };
    }

    if (_url.startsWith('?')) return { query: _url.slice(1) };

    const pathRx = /\/(?:(?<countryCode>[A-Za-z]{2}|private-office)\/)?(?<language>[A-Za-z]{2})(?:\/[^?]*)?(?:\?(?<query>.*))?$/;

    const match = _url.match(pathRx);

    if (!match?.groups) {
      if (!silent)
        console.warn(
          `'extractParamsFromUrl' called with invalid url. Should be '.../<country or 'private-office'>/<language>/...?<query>' but got: '${url}'`,
        );
      return {};
    }

    const countryCode =
      match.groups.countryCode === MarketCountryCode['private-office']
        ? MarketCountryCode['private-office']
        : match.groups.countryCode?.toUpperCase();

    const language = match.groups.language?.toLowerCase();

    return {
      countryCode: validateMarketCountryCode(countryCode) ? countryCode : undefined,
      language: validateLanguage(language)
        ? language
        : safePanic({ message: `Invalid language: ${language}. Falling back to Language.en`, fallback: Language.en }),
      query: match.groups.query,
    };
  } catch (error) {
    console.error(`Error extracting params from url: ${error}`);
    return {};
  }
};

export function flattenNestedValues(obj: Record<string, unknown>, prefix = ''): Record<string, unknown> {
  let result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj)) {
    if (isRecord(value)) {
      result = {
        ...result,
        ...flattenNestedValues(value, `${prefix}${key}.`),
      };
    } else {
      result[`${prefix}${key}`] = value;
    }
  }
  return result;
}

export function expandNestedValues(obj: Record<string, unknown>): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj)) {
    const keys = key.split('.');

    if (keys.some((k) => k === '__proto__' || k === 'constructor' || k === 'prototype')) {
      continue; // skip dangerous keys
    }

    const refinedValue = Array.isArray(value) ? [...new Set(value)].filter((v) => v !== '') : value;

    if (keys.length > SECURITY_LIMITS.MAX_NESTING_DEPTH) {
      console.error(`Nested object depth exceeds maximum limit: ${keys.length}. Rejecting.`);
      delete result[key];
    }

    if (keys.length > 1) {
      const lastKey = keys.pop()!;
      let nestedObj = result;
      for (const k of keys) {
        nestedObj[k] = nestedObj[k] || {};
        nestedObj = nestedObj[k] as Record<string, unknown>;
      }
      nestedObj[lastKey] = refinedValue;
    } else {
      result[key] = refinedValue;
    }
  }
  return result;
}

/**
 * Transform search state into search query params string
 * @param params object containing search state
 * @param currentParams string representing current query params
 * @returns string containing search query params
 */
export const stringifySearchParams = (params: Record<string, unknown>, currentParams?: string | URLSearchParams): string => {
  // normalize existing params (remove leading '?' if present)
  const searchParams = currentParams instanceof URLSearchParams ? currentParams : new URLSearchParams(currentParams);

  const _currentParams = searchParams.toString();

  const multiPolygon = params.multiPolygon;

  if (multiPolygon && isRecord(multiPolygon)) {
    params.multiPolygon = lzString.compressToEncodedURIComponent(JSON.stringify(multiPolygon.coordinates));
  }

  const nestedValues = flattenNestedValues(params);

  const newParams = qs.stringify(nestedValues, {
    skipNull: true,
    skipEmptyString: true,
    arrayFormat: 'bracket-separator',
  });

  if (!_currentParams && !newParams) return '';

  return `?${newParams}${_currentParams ? `&${_currentParams}` : ''}`;
};

export const getUrlWithParams = (url: string, params: Record<string, unknown>, preserveExistingParams = false) => {
  const [baseUrl, existingQuery] = url.split('?');
  const queryString = stringifySearchParams(params, preserveExistingParams ? existingQuery : '');

  return queryString ? `${baseUrl}${queryString}` : baseUrl;
};

/**
 * Transform search params string into options and filters objects
 * @param search query string containing search params
 * @param addFallbacksForRequiredFields boolean to add fallback values for required filters and options
 * @returns filters and options objects
 */
export function parseSearchParams<T = Record<string, unknown>>(
  url: string | URL,
  addFallbacksForRequiredFields?: false,
): ParsedSearchParams<T, false>;

export function parseSearchParams<T = Record<string, unknown>>(
  url: string | URL,
  addFallbacksForRequiredFields: true,
): ParsedSearchParams<T, true>;

export function parseSearchParams(url: string | URL, addFallbacksForRequiredFields?: boolean) {
  const { language, query, countryCode } = extractParamsFromUrl(url);

  if (query && query.length > SECURITY_LIMITS.MAX_QUERY_STRING_LENGTH) {
    console.warn(`Query string exceeds maximum length: ${query.length}. Full query: ${query}`);
  }

  const parsedQuery: Record<string, unknown> = qs.parse(query ?? '', {
    parseBooleans: true,
    parseNumbers: true,
    arrayFormat: 'bracket-separator',
    types: {
      masterDataShopIds: 'string[]',
      masterDataLiPaIds: 'string[]',
      shopIds: 'string[]',
    },
  });

  const expandedParams = expandNestedValues(parsedQuery);

  let { filters, options, additionalParams } = parseParams({
    ...expandedParams,
    ...(language && { language }),
  });

  if (countryCode) {
    options.marketCountryCode = countryCode;
  }

  if (addFallbacksForRequiredFields) {
    const { filters: refinedFilters, options: refinedOptions } = refineFiltersAndOptions(filters, options);

    options = refinedOptions;
    filters = refinedFilters;
  }

  return { options, filters, additionalParams };
}
